import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthPageLayout } from '@components/auth/AuthPageLayout';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { authService } from '@/services/auth';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(undefined);
    try {
      const result = await authService.requestPasswordReset(email);
      setSent(true);
      if (result.dev_reset_url) {
        setDevResetUrl(result.dev_reset_url);
      }
      toast.success('Check your inbox for reset instructions');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout
      title="Forgot password?"
      subtitle="Enter your email and we'll send you a link to reset your password."
      alternatePrompt="Remember your password?"
      alternateLinkTo="/login"
      alternateLinkLabel="Back to sign in"
      legalAction="requesting a reset"
    >
      {sent ? (
        <div className="space-y-4 text-center">
          <p className="text-sm text-khiimori-text-secondary leading-relaxed">
            If an account exists for <strong className="text-khiimori-text-primary">{email}</strong>, you will
            receive a password reset link shortly.
          </p>
          {devResetUrl && (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              Dev mode:{' '}
              <a href={devResetUrl} className="underline break-all">
                open reset link
              </a>
            </p>
          )}
          <Link to="/login" className="inline-block text-sm text-primary-400 hover:text-primary-300">
            Return to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Work email"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error}
            autoComplete="email"
            aria-required="true"
          />
          <Button type="submit" className="w-full" isLoading={isLoading}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthPageLayout>
  );
}
